import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { ReportUploadedFileDto } from "@/api/dtos";
import { ReportTags } from "./ReportTags";
import type { ReportUploadedFile, ReportUploadKind } from "./_models/ReportUpload";
import { toReportUploadedFileFromDto } from "./_transformers/toReportUploadedFileFromDto";

export interface GetReportUploadListArgs {
  report: ReportUploadKind;
  /** yyMMdd */
  date: string;
}

export const toReportUploadListTagId = (args: GetReportUploadListArgs): string => {
  return `${args.report}-${args.date}`;
};

/** The raw exports in S3 for one report and day (GET /reports/{report}/uploads?date=), in the order the snapshot merges them. */
export const getReportUploadListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getReportUploadList: builder.query<ReportUploadedFile[], GetReportUploadListArgs>({
      queryFn: async (args) => {
        try {
          const res = await axios.get<{ files: ReportUploadedFileDto[]; count: number }>(
            `${backendBase()}/reports/${args.report}/uploads`,
            { params: { date: args.date }, headers: { Accept: "application/json" } },
          );
          const files = res.data.files.map((x) => toReportUploadedFileFromDto(x));
          return { data: files };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (_result, _error, args) => [{ type: ReportTags.ReportUpload, id: toReportUploadListTagId(args) }],
    }),
  }),
});

export const getReportUploadList = getReportUploadListApi.endpoints.getReportUploadList;
